import styled from "@emotion/styled";
import React from "react";
import Text from "@components/Text";
import Button from "@components/Button";
import { Row } from "@components/Flex";

/**
 * Shared shell for an integration panel (John Deere, Cropwise, …).
 *
 * Exists so the panels can't drift apart visually: before this they were bare
 * <Column>s stacked one after another with only a small grey caption between
 * them, so two integrations read as one continuous block of settings.
 *
 * Card styling follows components/Card.tsx (white, 20px-blur shadow) with a
 * light border added — the settings page background is white, so the shadow
 * alone doesn't separate the card from the page.
 */
const Root = styled.div`
  display: flex;
  flex-direction: column;
  /* Fill the grid row so cards sitting side by side line up at the bottom. */
  height: 100%;
  background: #ffffff;
  border: 1px solid #ebeeef;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 20px rgba(78, 78, 81, 0.1);
  box-sizing: border-box;
`;

const Header = styled(Row)`
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

/**
 * Connection state at a glance, so a collapsed card still says whether it's
 * live without the user opening anything.
 */
const StatusPill = styled.span<{ connected?: boolean }>`
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  white-space: nowrap;
  color: ${({ connected }) => (connected ? "#137333" : "#899093")};
  background: ${({ connected }) => (connected ? "#E6F4EA" : "#F0F0F0")};
`;

interface IProps {
  title: string;
  /** Drives the status pill. Omit while the status is still loading. */
  connected?: boolean;
  connectedLabel?: string;
  disconnectedLabel?: string;
  children: React.ReactNode;
}

const IntegrationCard: React.FC<IProps> = ({
  title,
  connected,
  connectedLabel = "Connected",
  disconnectedLabel = "Not connected",
  children,
}) => (
  <Root>
    <Header>
      {/* type="heading" already applies bold — no inline fontWeight needed. */}
      <Text type="heading" size={18} color="#494C5B">
        {title}
      </Text>
      {connected !== undefined && (
        <StatusPill connected={connected}>
          {connected ? connectedLabel : disconnectedLabel}
        </StatusPill>
      )}
    </Header>
    {children}
  </Root>
);

const CollapsedRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface ICollapsedProps {
  /** One line on what connecting gets the user. */
  description: string;
  buttonLabel: string;
  onExpand: () => void;
}

/**
 * The disconnected, un-opened state of an integration card: a sentence and a
 * button, nothing else.
 *
 * The point is that setup instructions are only useful to someone actually
 * setting the integration up. A John Deere user has no reason to scroll past
 * Cropwise's three-step token walkthrough every time they open Settings, and
 * vice versa — so the walkthrough stays behind this button.
 */
export const CollapsedSetup: React.FC<ICollapsedProps> = ({
  description,
  buttonLabel,
  onExpand,
}) => (
  <CollapsedRoot>
    <Text size={13} color="#899093">
      {description}
    </Text>
    <Button kind="outline" onClick={onExpand} style={{ maxWidth: 200 }}>
      {buttonLabel}
    </Button>
  </CollapsedRoot>
);

export default IntegrationCard;
